import React, { useEffect, useState } from "react";
import {
    Box,
    Grid,
    Typography,
    Paper,
    IconButton,
    CircularProgress,
    Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { useForm } from "react-hook-form";

import WSSelectField from "../../../Components/Select/WSSelect";
import WSTextField, { TextType } from "../../../Components/TextBox/WSTextBox";
import { callAPI, Method } from "../../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmationPopup from "../../../Components/Confirmation/Confirmation";
import WSTextAreaField from "../../../Components/WSTextArea/WSTextArea";
import AddIcon from "@mui/icons-material/Add";


// ✅ TypeScript Interfaces
interface Item {
    ItemId: string;
    ItemName: string;
    ItemCode: string;
    IsActive: boolean;
}

interface Warehouse {
    WarehouseId: string;
    WarehouseName: string;
    Location: string;
    IsActive: boolean;
}

interface OpeningStockData {
    ItemId: string;
    WarehouseId: string;
    SupplierId: string;
    Quantity: number;
    Remarks: string;
}

const OpeningStock = () => {
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedId, setSelectedId] = useState<string | number>(0);
    const [isPopupOpen, setPopupOpen] = useState(false);
    const { showMessage } = useSnackbar();
    const params = useParams();
    const id = params.id;
    const navigate = useNavigate();

    // ✅ Properly typed state
    const [items, setItems] = useState<Item[]>([]);
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
    const [supplieres, setSuppliers] = useState<any[]>([]);

    const {
        control,
        handleSubmit,
        reset,
        formState: { isValid },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            ItemId: "",
            WarehouseId: "",
            Quantity: "",
            Remarks: "",
        },
    });



    const fetchAllData = async (url: string) => {
        let allData: any[] = [];
        let page = 1;
        let totalPages = 1;

        try {
            do {
                const response = await callAPI(
                    `${url}?page=${page}&pageSize=50`,
                    Method.GET
                );

                const data = response?.data;

                if (data?.results) {
                    allData = [...allData, ...data.results];
                    totalPages = data.totalPages || 1;
                }

                page++;
            } while (page <= totalPages);

            return allData;

        } catch (error) {
            console.error(`Error fetching ${url}:`, error);
            return [];
        }
    };

    // Fetch dropdown data
    const fetchDropdownData = async () => {
        try {
            setLoading(true);

            const [allItems, allWarehouses, allSuppliers] = await Promise.all([
                fetchAllData("items/list"),
                fetchAllData("warehouses/list"),
                fetchAllData("suppliers/list"),
            ]);

            setItems(allItems);
            setWarehouses(allWarehouses);
            setSuppliers(allSuppliers)

        } catch (error) {
            console.error("Failed to fetch dropdown data:", error);
            showMessage("Failed to load dropdown data", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {

        if (!id || id === "undefined" || id === "null" || id.trim() === "") {
            setIsEditMode(false);
            reset();
            fetchDropdownData();
            return;
        }

        setIsEditMode(true);
        fetchOpeningStockData(id);
        fetchDropdownData();
    }, [id, reset]);

    const fetchOpeningStockData = async (stockId: string) => {
        try {
            setLoading(true);

            const response = await callAPI(`opening-stock/${stockId}`, Method.GET);

            if (response?.success && response.data) {
                const stockData: OpeningStockData = response.data;

                reset({
                    ItemId: stockData.ItemId || "",
                    WarehouseId: stockData.WarehouseId || "",
                    Quantity: stockData.Quantity?.toString() || "",
                    Remarks: stockData.Remarks || "",
                });
            } else {
                console.error("❌ No data in response:", response);
                showMessage(response?.message || `Opening stock ${stockId} not found`, "error");
                setIsEditMode(false);
            }
        } catch (error: any) {
            console.error("❌ Fetch error:", error.response?.data || error.message);
            showMessage("Failed to load opening stock data", "error");
            setIsEditMode(false);
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = async (data: any) => {
        try {
            setLoading(true);
            const requestBody: OpeningStockData = {
                ItemId: data.ItemId || "",
                WarehouseId: data.WarehouseId || "",
                SupplierId: data.SupplierId || "",
                Quantity: parseInt(data.Quantity) || 0,
                Remarks: data.Remarks?.trim() || "",
            };

            console.log('body is', requestBody)
            let response;
            if (isEditMode && id) {
                response = await callAPI(`opening-stock/${id}`, Method.PATCH, requestBody);
            } else {
                response = await callAPI("opening-stock/", Method.POST, requestBody);
            }

            if (response?.success) {
                showMessage(
                    isEditMode ? "Opening stock updated successfully!" : "Opening stock created successfully!",
                    "success"
                );
                navigate("/openingstock/list");
            } else {
                showMessage(response?.message || "Operation failed", "error");
            }
        } catch (error: any) {
            console.error("❌ Submit error:", error);
            showMessage(error.response?.data?.detail || "Operation failed", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = () => {
        if (!id) return;
        setSelectedId(id);
        setPopupOpen(true);
    };

    return (
        <Box sx={{ p: 2, background: "#f0f0f0", minHeight: "100vh" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
                        {isEditMode ? "Open Stock Entry" : "Open Stock Entry"}
                    </Typography>
                </Box>
                <Box>

                    {isEditMode && (
                        <IconButton
                            sx={{ background: "black", color: "#fff" }}
                            onClick={handleDeleteClick}
                            disabled={loading}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            </Box>

            { }
            <Paper
                sx={{
                    borderRadius: "16px",
                    p: 3,
                    background: "#fafafa",
                    position: "relative"
                }}
            >
                {loading && (
                    <Box sx={{
                        position: "absolute",
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: "rgba(255,255,255,0.9)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        zIndex: 10, borderRadius: "16px"
                    }}>
                        <CircularProgress />
                    </Box>
                )}

                { }
                <Box display="flex" justifyContent="space-between" mb={7}>
                    <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
                        {isEditMode ? "Open Stock Details" : "Open Stock Details"}
                    </Typography>
                    <Tooltip title={isEditMode ? "Update Opening stock" : "Create Opening stock"} arrow>
                        <IconButton
                            sx={{ background: "#000", color: "#fff" }}
                            onClick={handleSubmit(onSubmit)}
                            disabled={loading}
                        >
                            <SaveIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Grid container rowSpacing={8} columnSpacing={5}>
                    { }
                    <Grid item xs={4}>
                        <WSSelectField
                            name="ItemId"
                            label="Item Name"
                            id="ItemId"
                            control={control}
                            inlineSearch
                            options={items.map(item => ({
                                value: item.ItemId,
                                label: `${item.ItemCode} - ${item.ItemName}`
                            }))}

                        />
                    </Grid>
                    <Grid item xs="auto" display="flex" alignItems="flex-start" sx={{ pl: '12px !important' }}>
                        <Box
                            onClick={() =>
                                navigate("/openingstock/itemmaster", { state: { from: "openingstock" } })
                            }
                            sx={{
                                height: "36px",
                                width: "36px",
                                borderRadius: "10px",
                                background: "#f3f3f3",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                flexShrink: 0,
                                border: "1px solid #d1d5db",
                                "&:hover": {
                                    background: "#e0e0e0",
                                },
                            }}
                        >
                            <AddIcon fontSize="small" />
                        </Box>
                    </Grid>

                    <Grid item xs={4}>
                        <WSSelectField
                            name="WarehouseId"
                            label="Warehouse"
                            id="WarehouseId"
                            control={control}
                            inlineSearch
                            options={warehouses.map(warehouse => ({
                                value: warehouse.WarehouseId,
                                label: warehouse.WarehouseName
                            }))}
                        />
                    </Grid>

                    <Grid item xs="auto" display="flex" alignItems="flex-start" sx={{ pl: '12px !important' }}>
                        <Box
                            onClick={() =>
                                navigate("/openingstock/Warehouse", { state: { from: "openingstock" } })
                            }
                            sx={{
                                height: "36px",
                                width: "36px",
                                borderRadius: "10px",
                                background: "#f3f3f3",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                flexShrink: 0,
                                border: "1px solid #d1d5db",
                                "&:hover": {
                                    background: "#e0e0e0",
                                },
                            }}
                        >
                            <AddIcon fontSize="small" />
                        </Box>
                    </Grid>
                    <Grid item xs={4}>
                        <WSTextField
                            name="Quantity"
                            label="Quantity"
                            id="Quantity"
                            control={control}
                            type={TextType.AllowOnlyNumber}
                        />
                    </Grid>

                    <Grid item xs={4}>
                        <WSSelectField
                            name="SupplierId"
                            label="Supplier"
                            id="SupplierId"
                            control={control}
                            inlineSearch
                            options={supplieres.map(supplier => ({
                                value: supplier.SupplierId,
                                label: supplier.SupplierName
                            }))}
                        />
                    </Grid>
                </Grid>

                { }
                <Grid item xs={6}>
                    <WSTextAreaField
                        name="Remarks"
                        label="Remarks"
                        id="Remarks"
                        control={control}
                    />
                </Grid>
            </Paper>
            <ConfirmationPopup
                type="DELETE"
                apiUrl="opeing-stock"
                id={selectedId}
                confirmMessage="Are you sure you want to delete this opening stock?"
                open={isPopupOpen}
                onClose={() => setPopupOpen(false)}
                onConfirm={async (data) => {
                    if (!selectedId) return;

                    if (data?.success === true) {

                        showMessage("opening stock deleted successfully!", "success");
                        navigate("/openingstock/list");
                    } else {
                        showMessage(data?.detail, "error");
                    }

                    setPopupOpen(false);
                }}
            />
        </Box>
    );
};

export default OpeningStock;